/**
 * Field-level journal of a transaction (ADR-009 §3.2 `changes`, §6.2 revert):
 * layers are projected to the semantic scene view, flattened to paths, and
 * compared before and after a commit.
 */
import type { FieldChange, LayerId } from '@maipo/agent-protocol';
import type { EditorDocument, GroupLayer } from '../core/model';
import { projectLayer } from './scene-view';

export interface Snapshot {
  /** Flattened SSV records of the projected layers. */
  records: Map<LayerId, Record<string, unknown>>;
  /** Every layer in tree order, projected or not. */
  order: LayerId[];
  ids: Set<LayerId>;
  /** Document-level state (canvas, guides, selection, extensions) for `layer: 'document'` changes. */
  document: DocumentRecord;
}

/** The pseudo layer id of document-level changes in a journal. */
export const DOCUMENT_JOURNAL_ID = 'document';

/**
 * Document-level state, cheap to capture on every commit: plain values are
 * compared by value; extension values and the selection by reference, because
 * every command that changes them assigns a new object (an extension value
 * may be a 32 MiB resource table, so it is never serialised here).
 */
export interface DocumentRecord {
  values: Record<string, unknown>;
  references: Map<string, unknown>;
}

export function documentRecord(document: EditorDocument): DocumentRecord {
  const references = new Map<string, unknown>();
  for (const [key, value] of Object.entries(document.extensions)) references.set(`extensions.${key}`, value);
  references.set('selection', document.selection);
  return {
    values: {
      'canvas.width': document.width,
      'canvas.height': document.height,
      'canvas.ppi': document.resolution.x,
      guides: (document.guides ?? []).map((guide) => ({ axis: guide.axis, position: guide.position })),
    },
    references,
  };
}

function documentChanges(before: DocumentRecord, after: DocumentRecord): FieldChange[] {
  const changes: FieldChange[] = [];
  for (const path of new Set([...Object.keys(before.values), ...Object.keys(after.values)])) {
    if (JSON.stringify(before.values[path] ?? null) !== JSON.stringify(after.values[path] ?? null)) changes.push({ layer: DOCUMENT_JOURNAL_ID, path, before: before.values[path] ?? null, after: after.values[path] ?? null });
  }
  for (const path of new Set([...before.references.keys(), ...after.references.keys()])) {
    const previous = before.references.get(path);
    const next = after.references.get(path);
    if (previous === next) continue;
    changes.push({ layer: DOCUMENT_JOURNAL_ID, path, before: previous === undefined ? null : 'present', after: next === undefined ? null : 'modified' });
    changes.push(...entryChanges(path, previous, next));
  }
  return changes;
}

/** Entries of an ID-keyed resource list (notes, slices, comps, …), or null when the value is not one. */
function keyedEntries(value: unknown): Map<string, unknown> | null {
  if (value === undefined) return new Map();
  if (!Array.isArray(value) || value.length > MAX_KEYED_ENTRIES) return null;
  const entries = new Map<string, unknown>();
  for (const entry of value) {
    const id = entry && typeof entry === 'object' ? (entry as { id?: unknown }).id : undefined;
    if (typeof id !== 'string') return null;
    entries.set(id, entry);
  }
  return entries;
}

const MAX_KEYED_ENTRIES = 1000;

/** Document resource lists whose entries are reported one by one (small, ID-keyed). */
const KEYED_RESOURCE_PATHS: ReadonlySet<string> = new Set([
  'maipo:notes', 'maipo:artboards', 'maipo:export-assets', 'maipo:slices', 'maipo:alpha-channels',
  'maipo:perspective-planes', 'maipo:layer-comps', 'maipo:global-swatches', 'maipo:actions',
  'maipo:tool-presets', 'maipo:brush-presets', 'maipo:graphic-styles',
].map((key) => `extensions.${key}`));

function entrySummary(entry: unknown): Record<string, unknown> {
  const record = entry as Record<string, unknown>;
  const summary: Record<string, unknown> = { id: record.id };
  for (const key of ['name', 'text', 'tool', 'color'] as const) {
    const value = record[key];
    if (typeof value === 'string') summary[key] = value.slice(0, 80);
  }
  return summary;
}

/**
 * Created, deleted and modified entries of a resource list that changed, as
 * `extensions.<key>[<id>]`: how an agent learns the ID of a note, slice or
 * comp its command created. Only lists that changed are examined.
 */
function entryChanges(path: string, previous: unknown, next: unknown): FieldChange[] {
  if (!KEYED_RESOURCE_PATHS.has(path)) return [];
  const before = keyedEntries(previous);
  const after = keyedEntries(next);
  if (!before || !after) return [];
  const changes: FieldChange[] = [];
  for (const [id, entry] of after) {
    const old = before.get(id);
    if (old === undefined) changes.push({ layer: DOCUMENT_JOURNAL_ID, path: `${path}[${id}]`, before: null, after: entrySummary(entry) });
    else if (JSON.stringify(old) !== JSON.stringify(entry)) changes.push({ layer: DOCUMENT_JOURNAL_ID, path: `${path}[${id}]`, before: 'present', after: 'modified' });
  }
  for (const [id, entry] of before) if (!after.has(id)) changes.push({ layer: DOCUMENT_JOURNAL_ID, path: `${path}[${id}]`, before: entrySummary(entry), after: null });
  return changes;
}

export const PIXELS_PREFIX = '\u0000pixels:';
/** Paths derived from others (bounds, decomposition, tree metrics) or session-only (provenance). */
export const JOURNAL_SKIPPED = /^(bounds|transform\.decomposed|childCount|depth|provenance)(\.|$)/;

export function flattenRecord(value: unknown, path: string, into: Record<string, unknown>): void {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) flattenRecord(child, path ? `${path}.${key}` : key, into);
    return;
  }
  if (Array.isArray(value) && path === 'objects') {
    for (const object of value as { id: string }[]) flattenRecord(object, `objects[${object.id}]`, into);
    return;
  }
  into[path] = value;
}

/**
 * Projects the document's layers. With `include`, only matching layers are
 * projected (the UI path journals just a transaction's footprint), but every
 * ID is still listed so creations and deletions are detected.
 */
export function snapshot(document: EditorDocument, include?: (id: LayerId) => boolean): Snapshot {
  const records = new Map<LayerId, Record<string, unknown>>();
  const order: LayerId[] = [];
  const visit = (group: GroupLayer, parentId: LayerId | null, depth: number) => {
    group.children.forEach((layer, index) => {
      order.push(layer.id);
      if (!include || include(layer.id)) {
        const view = projectLayer(document, layer, { parentId, index, depth }, { textLimit: Infinity });
        const record: Record<string, unknown> = {};
        flattenRecord({ ...view, bounds: undefined }, '', record);
        // Pixels never enter the view; tile-store revisions let the journal say that they changed.
        if (layer.kind === 'raster') record[PIXELS_PREFIX + 'raster'] = layer.raster.storeRevision();
        if (layer.mask) record[PIXELS_PREFIX + 'mask'] = layer.mask.tiles.storeRevision();
        records.set(layer.id, record);
      }
      if (layer.kind === 'group') visit(layer, layer.id, depth + 1);
    });
  };
  visit(document.root, null, 0);
  return { records, order, ids: new Set(order), document: documentRecord(document) };
}

/** The flattened record of one layer as it is now, or undefined when it no longer exists. */
export function currentRecord(document: EditorDocument, id: LayerId): Record<string, unknown> | undefined {
  return snapshot(document, (candidate) => candidate === id).records.get(id);
}

export function diff(before: Snapshot, after: Snapshot, writes: ReadonlySet<LayerId>): { changes: FieldChange[]; created: LayerId[]; deleted: LayerId[]; modified: LayerId[] } {
  const changes: FieldChange[] = [];
  const created = after.order.filter((id) => !before.ids.has(id));
  const deleted = before.order.filter((id) => !after.ids.has(id));
  const summary = (record: Record<string, unknown>) => ({ kind: record.kind, name: record.name, parentId: record.parentId, index: record.index });
  for (const id of created) changes.push({ layer: id, path: '(created)', before: null, after: summary(after.records.get(id) ?? {}) });
  for (const id of deleted) changes.push({ layer: id, path: '(deleted)', before: summary(before.records.get(id) ?? {}), after: null });
  const modified: LayerId[] = [];
  for (const id of after.order) {
    const previous = before.records.get(id);
    const next = after.records.get(id);
    if (!previous || !next) continue;
    let touched = false;
    for (const path of new Set([...Object.keys(previous), ...Object.keys(next)])) {
      if (JOURNAL_SKIPPED.test(path) || next[path] === undefined && previous[path] === undefined) continue;
      if (path === 'index' && !writes.has(id)) continue;
      if (path.startsWith(PIXELS_PREFIX)) {
        if (previous[path] === undefined || next[path] === undefined || previous[path] === next[path]) continue;
        changes.push({ layer: id, path: `${path.slice(PIXELS_PREFIX.length)}.pixels`, before: null, after: 'modified' });
        touched = true;
        continue;
      }
      if (JSON.stringify(previous[path]) === JSON.stringify(next[path])) continue;
      changes.push({ layer: id, path, before: previous[path] ?? null, after: next[path] ?? null });
      touched = true;
    }
    if (touched) modified.push(id);
  }
  changes.push(...documentChanges(before.document, after.document));
  return { changes, created, deleted, modified };
}
